import { FC } from "react";
import { TableCell, TableHead } from "@capacit-com/lara-law-shared-ui";
import FavoriteButton from "@/components/buttons/FavoriteButton";
import { DocumentCategory } from "@/models/legislation/Legislation";
import {
  canToggleFavorite,
  FavoriteSourceTarget,
} from "../lib/favoriteSourceRows";

interface SourceFavoriteCellProps {
  /** Null for rows that cannot be favorited — the cell keeps the column aligned. */
  target: FavoriteSourceTarget | null;
  isFavorite: boolean;
  isHovering: boolean;
}

/**
 * Header for the favorite column; the star itself carries the label. The
 * narrow padding matches the body cell so the column keeps one width whether
 * or not a row currently shows a star.
 */
export const SourceFavoriteHead: FC = () => (
  <TableHead className="w-10 min-w-8 px-1 text-xs h-8">
    <span className="sr-only">Favorit</span>
  </TableHead>
);

/**
 * Favorite toggle for a source row in a dashboard list widget. The button
 * shows itself only while the row is hovered, favorited, or mid-request.
 */
const SourceFavoriteCell: FC<SourceFavoriteCellProps> = ({
  target,
  isFavorite,
  isHovering,
}) => (
  // The cell's default p-3 is wider than this column ever gets, which would
  // push the 24px star past the column edge — under a widget's vertical
  // scrollbar once the table scrolls horizontally. min-w-8 holds the column at
  // the star's own width so the row does not reflow when the star appears, and
  // py-0 keeps it from making the row taller than the text rows around it.
  <TableCell className="w-10 min-w-8 px-1 py-0 text-center">
    {canToggleFavorite(target, isFavorite) && (
      <FavoriteButton
        isHovering={isHovering}
        documentCategory={DocumentCategory.Legislation}
        documentId={target.externalId}
        title={target.title}
        datasource={target.datasource}
        internalId={target.internalId}
        isFavorite={isFavorite}
        className="size-6"
      />
    )}
  </TableCell>
);

export default SourceFavoriteCell;
